'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { ThemePreference } from '../../../../packages/types/src/index';

type ResolvedTheme = 'dark';

interface ThemeContextType {
  theme: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'astropredict_theme';

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme] = useState<ThemePreference>('dark');
  const [resolvedTheme] = useState<ResolvedTheme>('dark');

  useEffect(() => {
    // Delete any old saved theme preference from local storage so returning visitors see dark
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.removeItem(THEME_STORAGE_KEY);
      }
    } catch (_) {}

    // Enforce dark on documentElement
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', 'dark');
      document.documentElement.classList.remove('light');
      document.documentElement.classList.add('dark');
    }
  }, []);

  const setTheme = (_newTheme: ThemePreference) => {
    // Permanent dark theme: no-op
  };

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    return {
      theme: 'dark' as const,
      resolvedTheme: 'dark' as const,
      setTheme: () => {}
    };
  }
  return context;
}
